/**
 * Utility to determine data requirements based on current route
 */

export type DataRequirementType =
	| 'core'
	| 'trading'
	| 'portfolio'
	| 'stats'
	| 'vaults'
	| 'earn'
	| 'referrals'
	| 'transacting'; // Pages where users send transactions (trading + portfolio + earn + vaults)

export interface RouteDataRequirements {
	core: boolean; // Always needed (wallet, settings, theme)
	trading: boolean; // Trading-related data (orders, candles, events)
	portfolio: boolean; // User account data, positions
	stats: boolean; // Statistics and analytics
	vaults: boolean; // Vault-specific data
	earn: boolean; // Earn page features
	referrals: boolean; // Referral system
	transacting: boolean; // Transaction-heavy pages (priority fees, account polling)
}

/**
 * Determines data requirements based on pathname
 */
function getRouteDataRequirements(pathname: string): RouteDataRequirements {
	const requirements: RouteDataRequirements = {
		core: true, // Always needed
		trading: false,
		portfolio: false,
		stats: false,
		vaults: false,
		earn: false,
		referrals: false,
		transacting: false,
	};

	// Trading pages
	if (
		pathname === '/' ||
		pathname.includes('/perpetual') ||
		pathname.includes('/spot') ||
		pathname.includes('/swap') ||
		pathname.match(/^\/[A-Za-z0-9]+-PERP/) ||
		pathname.match(/^\/[A-Za-z0-9]+$/) || // Spot markets like /SOL, /ETH, /1KWEN
		pathname.match(/^\/ref\//) // Referral code routes (/ref/<code>) render the trade page
	) {
		requirements.trading = true;
		requirements.portfolio = true; // Trading pages need user data too
		requirements.transacting = true; // Users can place orders
	}

	// Portfolio pages
	if (pathname.includes('/portfolio')) {
		requirements.portfolio = true;
		requirements.transacting = true; // Users can deposit, withdraw, transfer
	}

	// Stats pages
	if (pathname.includes('/stats')) {
		requirements.stats = true;
		requirements.portfolio = true; // Stats pages may need user data for personal stats
		// No transacting - stats pages are read-only
	}

	// Strategy-vaults pages only — Insurance Fund Vaults (/vaults/insurance-fund-vaults)
	// do not use the strategy-vault data layer and are handled separately below.
	if (pathname.includes('/vaults/strategy-vaults')) {
		requirements.vaults = true;
		requirements.transacting = true; // Users can deposit/withdraw from vaults
	}

	// Insurance Fund Vaults pages
	if (pathname.includes('/vaults/insurance-fund-vaults')) {
		requirements.transacting = true; // Users can stake/unstake into IF vaults
	}

	// Earn pages
	if (pathname.includes('/earn')) {
		requirements.earn = true;
		requirements.transacting = true; // Users can stake/unstake
	}

	// Velocity Safety Module (staking)
	if (pathname.includes('/drift-safety-module')) {
		requirements.earn = true; // Reuse earn data requirements for staking functionality
		requirements.portfolio = true; // May need user balance and position data
		requirements.transacting = true; // Users can stake/unstake
	}

	// Referrals pages
	if (pathname.includes('/referrals')) {
		requirements.referrals = true;
		// No transacting - referrals pages are mostly read-only
	}

	return requirements;
}

/**
 * Checks if a specific data requirement is needed for the current route
 */
export function needsDataRequirement(
	pathname: string,
	requirement: DataRequirementType
): boolean {
	const requirements = getRouteDataRequirements(pathname);
	return requirements[requirement];
}
